import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { editGameReducer } from './editGameReducer';
import {
	EditGameContext,
	editGameInitialState,
	TEditGameSelectedItem,
	TEditGameVisibleForm,
	TSourceUrlLocally,
	TTypeOfEditGameModalWindow,
} from './editGameContext';
import { EditGameActionTypes } from './editGameActions';
import {
	IItemForGetShortInfo,
	TFileId,
	TLanguageIDs,
	IFileData,
	IServerError,
	IGameApplicationIdValidationError,
	IEditGame,
	IGameTranslation,
	IGetGameInfo,
} from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getGameInfo } from 'backend/games';

const EditGameState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editGameReducer, editGameInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const gameID = params.get('gameID');

	const { response, loading, refetch } = useFetchData<IGetGameInfo, string>(
		gameID ? getGameInfo : null,
		gameID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditGameActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.game.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditGameActionTypes.SET_ALL_DATA,
				payload: {
					gameID: response.data.game.id,
					name: response.data.game.name || '',
					applicationId: response.data.game.applicationId || '',
					IDSelectedCategory: response.data.game.gameCategoryId,
					IDSelectedDeveloper: response.data.game.developerId,
					description: response.data.game.description || '',
					appStoreLink: response.data.game.appStoreLink || '',
					playMarketLink: response.data.game.playMarketLink || '',
					versionCode: String(response.data.game.versionCode),
					versionName: response.data.game.versionName || '',
					IDsSelectedLanguages: response.data.game.translations.map((el) => el.languageId),
					translations: response.data.game.translations,
					iconPath: response.data.game.iconPath || '',
					gamePath: response.data.game.gamePath || '',
				},
			});
			dispatch({
				type: EditGameActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.game.name || '',
						applicationId: response.data.game.applicationId || '',
						IDSelectedCategory: response.data.game.gameCategoryId,
						IDSelectedDeveloper: response.data.game.developerId,
						description: response.data.game.description || '',
						appStoreLink: response.data.game.appStoreLink || '',
						playMarketLink: response.data.game.playMarketLink || '',
						versionCode: String(response.data.game.versionCode),
						versionName: response.data.game.versionName || '',
						IDsSelectedLanguages: response.data.game.translations.map((el) => el.languageId),
						translations: response.data.game.translations,
						iconFilePath: response.data.game.iconPath || '',
						gameFileData: { fileId: '', fileName: '', fileHash: null },
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditGameActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditGameVisibleForm) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFile = useCallback((fileId: TFileId, sourceUrlLocally: TSourceUrlLocally) => {
		dispatch({
			type: EditGameActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditGameActionTypes.DELETE_PHOTO }), []);

	const setFetchedDevelopers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditGameActionTypes.SET_FETCHED_DEVELOPERS,
			payload: {
				developers: value,
			},
		});
	}, []);

	const setIDDeveloper = useCallback((value: TEditGameSelectedItem) => {
		dispatch({
			type: EditGameActionTypes.SET_ID_DEVELOPER,
			payload: {
				IDDeveloper: value,
			},
		});
	}, []);

	const refetchDevelopers = useCallback((needRefetch: boolean) => {
		dispatch({
			type: EditGameActionTypes.REFETCH_DEVELOPERS,
			payload: {
				needRefetch,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditGameActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				categories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: TEditGameSelectedItem) => {
		dispatch({
			type: EditGameActionTypes.SET_ID_CATEGORY,
			payload: {
				IDCategory: value,
			},
		});
	}, []);

	const changeApplicationId = useCallback((value: string) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_APPLICATION_ID,
			payload: {
				applicationId: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeVersionCode = useCallback((value: string) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_VERSION_CODE,
			payload: {
				versionCode: value,
			},
		});
	}, []);

	const changeVersionName = useCallback((value: string) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_VERSION_NAME,
			payload: {
				versionName: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const changeAppStoreLink = useCallback((value: string) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_APP_STORE_LINK,
			payload: {
				appStoreLink: value,
			},
		});
	}, []);

	const changePlayMarketLink = useCallback((value: string) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_PLAY_MARKET_LINK,
			payload: {
				playMarketLink: value,
			},
		});
	}, []);

	const changeFileData = useCallback((value: IFileData) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_FILE_DATA,
			payload: {
				fileData: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditGameActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				languages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: EditGameActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IGameTranslation = {
						languageId: newTranslationID,
						name: '',
						description: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: EditGameActionTypes.SET_TRANSLATION_FIELDS,
						payload: {
							updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: EditGameActionTypes.SET_TRANSLATION_FIELDS,
					payload: {
						updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IEditGame | null) => {
		dispatch({
			type: EditGameActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditGameModalWindow) => {
		dispatch({
			type: EditGameActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<IGameApplicationIdValidationError> | null) => {
		dispatch({
			type: EditGameActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditGameActionTypes.SET_TRANSLATION_TAB,
			payload: {
				translationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditGameActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: EditGameActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const resetFields = useCallback(() => dispatch({ type: EditGameActionTypes.RESET_FIELDS }), []);

	return (
		<EditGameContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				setFile,
				deletePhoto,
				setFetchedDevelopers,
				setIDDeveloper,
				refetchDevelopers,
				setFetchedCategories,
				setIDCategory,
				changeApplicationId,
				changeName,
				changeVersionCode,
				changeVersionName,
				changeDescription,
				changeAppStoreLink,
				changePlayMarketLink,
				changeFileData,
				setFetchedLanguages,
				setIDsLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				gameID: state.gameID,
				visibleForm: state.visibleForm,
				iconFilePath: state.iconFilePath,
				iconFileId: state.iconFileId,
				sourceUrlLocally: state.sourceUrlLocally,
				fetchedDevelopers: state.fetchedDevelopers,
				IDSelectedDeveloper: state.IDSelectedDeveloper,
				needRefetchDevelopers: state.needRefetchDevelopers,
				fetchedCategories: state.fetchedCategories,
				IDSelectedCategory: state.IDSelectedCategory,
				applicationId: state.applicationId,
				name: state.name,
				versionCode: state.versionCode,
				versionName: state.versionName,
				description: state.description,
				appStoreLink: state.appStoreLink,
				playMarketLink: state.playMarketLink,
				gameFilePath: state.gameFilePath,
				gameFileData: state.gameFileData,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
			}}>
			{children}
		</EditGameContext.Provider>
	);
};

export default EditGameState;
